/**
 * Narrowing a wire string to a closed vocabulary, stated once (015 `/simplify`).
 *
 * The generated contract carries every closed vocabulary as a plain `string` — the OpenAPI schema
 * has the enum, but `schema.d.ts` widens it — so each one needs a seam on arrival where an
 * unrecognised spelling is refused. 015 wrote three of them in one cycle (the date format family,
 * the title date variant, the theme), because each is six lines and six lines never look like
 * duplication until the third copy disagrees with the other two. It did: the theme's spelling
 * tested membership with `includes`, which tells the compiler nothing, so the value had to be cast
 * back to the narrow type afterwards.
 *
 * It **throws rather than clamps**, which is the whole point and is the same rule
 * `PractitionerPreferences.Restore` states server-side: falling back to a default for a value this
 * client does not know would mask a client/server version mismatch on every surface at once, and
 * mask it as ordinary behaviour. The one caller that must not die of it —
 * `SessionStore.adopt()`, which has already spent the refresh token by the time it paints — catches
 * it there, deliberately and at that one site.
 */
export const narrowTo =
  <T extends string>(vocabulary: readonly T[], label: string) =>
  (value: string): T => {
    // `find` over the array, never a lookup: an object keyed by member would answer for
    // `'constructor'` and every other prototype property, and `includes` proves nothing to the
    // compiler. This form both refuses those and returns `T`, so no caller needs a cast.
    const member = vocabulary.find((candidate) => candidate === value);
    if (member === undefined) throw new Error(`Unknown ${label}: ${value}`);
    return member;
  };

/**
 * The same narrowing as a PREDICATE, for the callers that must test rather than throw.
 *
 * `narrowTo` above throws by design, which is right for a wire value and wrong for a validity check —
 * `core/storage/ui-state.ts` takes an `isValid` guard and a stored word this build does not recognise
 * is a thing to ignore, not an outage. 030 needed exactly that and wrote
 * `(GROUP_BY_VALUES as readonly string[]).includes(value)` locally: the `includes`-plus-cast shape
 * this module's own header records as the mistake 015 consolidated away. So it lives here instead.
 *
 * `some` over the array rather than an object lookup or `includes`, for the two reasons stated above:
 * a keyed object answers for `'constructor'`, and `includes` proves nothing to the compiler — this
 * form returns a real type guard with no cast at the call site.
 */
export const isMemberOf =
  <T extends string>(vocabulary: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && vocabulary.some((candidate) => candidate === value);
